import React, { useState } from 'react'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import { Icon, Typography, CircularProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { FormattedMessage } from 'react-intl'
import clsx from 'clsx'
import AvatarWithFallback from '../AvatarWithFallback'
import { compressImage } from 'libs/compressImage'
import PersonAvatar from '../PersonAvatar'

const useStyles = makeStyles((theme) => ({
  root: {
    '& #hover-area:hover + label': {
      top: '0% !important',
    },
    '& label:not(:hover)': {
      top: '100%',
    },
  },
  link: {
    color: theme.palette.primary.main,
  },
  text: {
    fontSize: 12,
    color: theme.palette.primary.text,
  },
  icon: {
    fontSize: 18,
    color: theme.palette.primary.text,
  },
  uploadButton: {
    borderBottomLeftRadius: '48px',
    borderBottomRightRadius: '48px',
    backgroundColor: 'rgb(28,30,33, 0.8)',
  },
  loadingBackground: {
    borderRadius: '48px',
    backgroundColor: 'rgb(28,30,33, 0.8)',
  },
  avatarIcon: {
    width: '100%',
    height: '100%',
    fontSize: '35px',
  },
}))

const resizeImage = (onChange) => async (file) => {
  const newFile = await compressImage(file)
  return R.unless(R.isNil, onChange)(newFile)
}

const UploadAvatar = ({
  src,
  isURL = false,
  onChange,
  isLoading = false,
  disabled = false,
  ...rest
}) => {
  const classes = useStyles(rest)
  const [randomName] = useState(Math.random())

  function renderUploadControl() {
    if (disabled) return null
    return (
      <>
        {isLoading && (
          <div
            className={clsx(
              classes.loadingBackground,
              'absolute flex justify-center items-center w-full h-full overflow-hidden',
            )}
          >
            <CircularProgress className="absolute " />
          </div>
        )}
        <div
          className={clsx(
            classes.root,
            'absolute w-96 h-48 bottom-0 overflow-hidden',
          )}
        >
          <div id="hover-area" className="absolute w-full h-full"></div>
          <label
            htmlFor={randomName}
            className={clsx(
              classes.link,
              'absolute w-full h-full cursor-pointer',
            )}
          >
            <div
              className={clsx(
                classes.uploadButton,
                'absolute flex justify-center items-center w-full h-full',
              )}
            >
              <div className="flex flex-col items-center">
                <Icon className={classes.icon}>photo_camera</Icon>
                <Typography className={classes.text}>
                  <FormattedMessage id="action.update" />
                </Typography>
              </div>
            </div>
          </label>
          <input
            type="file"
            id={randomName}
            accept="image/png, image/jpeg"
            className="invisible absolute"
            onChange={R.compose(
              resizeImage(onChange),
              R.unless(R.isNil, R.head),
              R.view(R.lensPath(['target', 'files'])),
            )}
          />
        </div>
      </>
    )
  }

  return (
    <div className="w-96 h-96 relative">
      {typeof src === 'object' ? (
        <PersonAvatar
          placeHolder={src.avCharacters}
          subId={500}
          alt={src.name}
          className={classes.avatarIcon}
        />
      ) : (
        <AvatarWithFallback
          src={src}
          className="absolute w-96 h-96"
          {...rest}
        />
      )}
      {renderUploadControl()}
    </div>
  )
}

UploadAvatar.propTypes = {
  src: PropTypes.string,
  onChange: PropTypes.func,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
}

export default UploadAvatar
